import React, { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const ResultsDisplay = ({ results, theme, isDarkTheme }) => {
  const [activeResultTab, setActiveResultTab] = useState('chart');
  const MetricCard = ({ label, value, positive, negative, icon }) => {
    const valueColor = positive ? '#0f9d58' : negative ? '#d93025' : (theme?.textPrimary || '#333');

    return (
      <div style={{
        backgroundColor: isDarkTheme ? '#333' : '#fafafa',
        border: `1px solid ${theme?.border || '#e0e0e0'}`,
        borderRadius: '6px',
        padding: '15px',
        transition: 'all 0.2s'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}>
        <div style={{ fontSize: '11px', color: theme?.textSecondary || '#666', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          {icon && <span>{icon}</span>}
          {label}
        </div>
        <div style={{ fontSize: '22px', fontWeight: 600, color: valueColor }}>
          {value}
        </div>
      </div>
    );
  };

  if (!results) {
    return null;
  }

  return (
    <div style={{ borderTop: `1px solid ${theme?.border || '#e0e0e0'}`, marginTop: '30px', paddingTop: '30px' }}>
      <h3 style={{ fontSize: '16px', fontWeight: 500, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px', color: theme?.textPrimary || '#333' }}>
        <span style={{ fontSize: '20px' }}>✅</span>
        Backtest Results
      </h3>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '15px',
        marginBottom: '20px'
      }}>
        <MetricCard label="Total Trades" value={results.totalTrades} icon="🔢" />
        <MetricCard label="Win Rate" value={`${results.winRate}%`} icon="🎯" />
        <MetricCard label="Profit Factor" value={results.profitFactor} icon="📊" />
        <MetricCard 
          label="Net Profit" 
          value={parseFloat(results.netProfit).toLocaleString()} 
          positive={parseFloat(results.netProfit) > 0}
          negative={parseFloat(results.netProfit) < 0}
          icon="💰"
        />
        <MetricCard label="Max Drawdown" value={`-${results.maxDrawdown}%`} negative icon="📉" />
        <MetricCard label="Sharpe Ratio" value={results.sharpeRatio} icon="📈" />
        <MetricCard 
          label="Initial Capital" 
          value={parseFloat(results.equityCurve?.[0]?.value || 0).toLocaleString()} 
          icon="💵"
        />
        <MetricCard 
          label="Execution Time" 
          value={results.executionTime ? `${results.executionTime}ms` : 'N/A'} 
          icon="⏱️"
        />
      </div>

      {/* Tabbed Interface for Chart and Trades */}
      <div style={{
        padding: '20px',
        backgroundColor: isDarkTheme ? '#2d2d2d' : '#fafafa',
        borderRadius: '6px',
        border: `1px solid ${theme?.border || '#e0e0e0'}`,
        marginTop: '10px'
      }}>
        {/* Tab Navigation */}
        <div style={{ display: 'flex', borderBottom: `2px solid ${theme?.border || '#e0e0e0'}`, marginBottom: '20px' }}>
          <button
            onClick={() => setActiveResultTab('chart')}
            style={{
              padding: '12px 24px',
              border: 'none',
              background: 'none',
              borderBottom: activeResultTab === 'chart' ? '2px solid #4285f4' : 'none',
              color: activeResultTab === 'chart' ? '#4285f4' : (theme?.textSecondary || '#666'),
              fontWeight: activeResultTab === 'chart' ? 600 : 400,
              cursor: 'pointer',
              marginBottom: '-2px',
              fontSize: '14px',
              transition: 'color 0.2s'
            }}
          >
            📈 Equity Curve
          </button>
          {results.trades && results.trades.length > 0 && (
            <button
              onClick={() => setActiveResultTab('trades')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: activeResultTab === 'trades' ? '2px solid #4285f4' : 'none',
                color: activeResultTab === 'trades' ? '#4285f4' : (theme?.textSecondary || '#666'),
                fontWeight: activeResultTab === 'trades' ? 600 : 400,
                cursor: 'pointer',
                marginBottom: '-2px',
                fontSize: '14px',
                transition: 'color 0.2s'
              }}
            >
              � Trades ({results.trades.length})
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeResultTab === 'chart' && results.equityCurve && results.equityCurve.length > 0 && (
          <div>
            <ResponsiveContainer width="100%" height={450}>
              <LineChart data={results.equityCurve} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={theme?.border || "#e0e0e0"} />
                <XAxis 
                  dataKey="date" 
                  stroke={theme?.textSecondary || "#666"} 
                  style={{ fontSize: '11px' }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                />
                <YAxis 
                  stroke={theme?.textSecondary || "#666"} 
                  style={{ fontSize: '11px' }}
                  tickFormatter={(value) => `${(value/1000).toFixed(1)}K`}
                  domain={['dataMin - 500', 'dataMax + 500']}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: theme?.cardBg || 'white',
                    border: `1px solid ${theme?.border || '#ddd'}`,
                    borderRadius: '4px',
                    fontSize: '12px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    color: theme?.textPrimary || '#333'
                  }}
                  formatter={(value, name) => [
                    value.toLocaleString(), 
                    'Portfolio Value'
                  ]}
                  labelFormatter={(label) => {
                    const date = new Date(label);
                    return `Date: ${date.toLocaleDateString()}`;
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="value" 
                  stroke="#4285f4" 
                  strokeWidth={3}
                  dot={false}
                  name="Portfolio Value"
                  activeDot={{ r: 6, fill: '#4285f4' }}
                />
              </LineChart>
            </ResponsiveContainer>
            
            <div style={{ 
              marginTop: '15px', 
              padding: '10px', 
              backgroundColor: 'white',
              borderRadius: '4px',
              fontSize: '12px',
              color: '#666',
              textAlign: 'center'
            }}>
              <strong>Performance Summary:</strong> Initial Capital: {parseFloat(results.equityCurve[0]?.value || 0).toLocaleString()} 
              → Final Value: {parseFloat(results.equityCurve[results.equityCurve.length - 1]?.value || 0).toLocaleString()} 
              ({parseFloat(results.netProfit) > 0 ? '+' : ''}{results.totalTrades > 0 ? ((parseFloat(results.equityCurve[results.equityCurve.length - 1]?.value || 0) / parseFloat(results.equityCurve[0]?.value || 1) - 1) * 100).toFixed(2) : 0}%)
            </div>
          </div>
        )}

        {activeResultTab === 'trades' && results.trades && results.trades.length > 0 && (
          <div>
            <div style={{
              backgroundColor: theme?.cardBg || 'white',
              borderRadius: '6px',
              border: `1px solid ${theme?.border || '#e0e0e0'}`,
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '90px 90px 80px 80px 70px 70px 90px 180px',
                padding: '12px 15px',
                backgroundColor: isDarkTheme ? '#404040' : '#f8f9fa',
                borderBottom: `1px solid ${theme?.border || '#e0e0e0'}`,
                fontSize: '11px',
                fontWeight: 600,
                color: theme?.textSecondary || '#666'
              }}>
                <div>Entry Date</div>
                <div>Exit Date</div>
                <div>Entry Price</div>
                <div>Exit Price</div>
                <div>Quantity</div>
                <div>P&L</div>
                <div>Return %</div>
                <div>Exit Reason</div>
                <div>Indicators</div>
              </div>
              
              {/* Table Body */}
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {results.trades.map((trade, idx) => (
                  <div 
                    key={idx} 
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '90px 90px 80px 80px 70px 70px 90px 90px 180px',
                      padding: '12px 15px',
                      borderBottom: idx < results.trades.length - 1 ? `1px solid ${theme?.border || '#f0f0f0'}` : 'none',
                      fontSize: '15px',
                      backgroundColor: idx % 2 === 0 ? (theme?.cardBg || 'white') : (isDarkTheme ? '#333' : '#fafafa'),
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = isDarkTheme ? '#404040' : '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = idx % 2 === 0 ? (theme?.cardBg || 'white') : (isDarkTheme ? '#333' : '#fafafa');
                    }}
                  >
                    <div style={{ color: theme?.textPrimary || '#333' }}>
                      {new Date(trade.entry_date).toLocaleDateString()}
                    </div>
                    <div style={{ color: theme?.textPrimary || '#333' }}>
                      {new Date(trade.exit_date).toLocaleDateString()}
                    </div>
                    <div style={{ color: theme?.textSecondary || '#666', fontFamily: 'monospace' }}>
                      {trade.entry_price?.toFixed(2)}
                    </div>
                    <div style={{ color: theme?.textSecondary || '#666', fontFamily: 'monospace' }}>
                      {trade.exit_price?.toFixed(2)}
                    </div>
                    <div style={{ color: theme?.textSecondary || '#666', fontFamily: 'monospace' }}>
                      {trade.quantity}
                    </div>
                    <div style={{ 
                      color: trade.pnl > 0 ? '#0f9d58' : '#d93025', 
                      fontWeight: 600,
                      fontFamily: 'monospace'
                    }}>
                      {trade.pnl > 0 ? '+' : ''}{trade.pnl?.toFixed(2)}
                    </div>
                    <div style={{ 
                      color: trade.return_pct > 0 ? '#0f9d58' : '#d93025', 
                      fontWeight: 500,
                      fontFamily: 'monospace'
                    }}>
                      {trade.return_pct > 0 ? '+' : ''}{trade.return_pct?.toFixed(2)}%
                    </div>
                    <div style={{ 
                      color: theme?.textPrimary || '#333',
                      fontSize: '15px',
                      fontWeight: 500
                    }}>
                      {trade.exit_reason || 'Exit Condition'}
                    </div>
                    <div style={{ 
                      fontSize: '15px',
                      lineHeight: '1.4',
                      padding: '4px'
                    }}>
                      {trade.indicators && Object.keys(trade.indicators).length > 0 
                        ? (() => {
                            const indicators = trade.indicators;
                            const entryIndicators = [];
                            const exitIndicators = [];
                            
                            // Separate entry and exit indicators
                            const ignore = ['open', 'high', 'low', 'close', 'volume'];
                            const isRealIndicator = (name) => {
                              const lower = name.toLowerCase();
                              return !ignore.includes(lower) && !lower.startsWith('temp');
                            };
                            Object.entries(indicators).forEach(([key, value]) => {
                              const formattedValue = typeof value === 'number' ? value.toFixed(1) : value;
                              if (key.startsWith('entry_')) {
                                const cleanKey = key.replace('entry_', '').replace('_', '');
                                if (isRealIndicator(cleanKey)) {
                                  entryIndicators.push({ key: cleanKey, value: formattedValue });
                                }
                              } else if (key.startsWith('exit_')) {
                                const cleanKey = key.replace('exit_', '').replace('_', '');
                                if (isRealIndicator(cleanKey)) {
                                  exitIndicators.push({ key: cleanKey, value: formattedValue });
                                }
                              }
                            });
                            
                            return (
                              <div title={`Entry: ${entryIndicators.map(i => `${i.key}=${i.value}`).join(', ')} | Exit: ${exitIndicators.map(i => `${i.key}=${i.value}`).join(', ')}`}>
                                {entryIndicators.length > 0 && (
                                  <div style={{ 
                                    color: isDarkTheme ? '#81c784' : '#2e7d32', 
                                    fontWeight: '600', 
                                    marginBottom: '3px',
                                    fontSize: '15px'
                                  }}>
                                    � Entry: {entryIndicators.map(i => (
                                      <span key={i.key} style={{ 
                                        backgroundColor: isDarkTheme ? '#1b5e20' : '#e8f5e8',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        marginRight: '6px',
                                        fontFamily: 'monospace',
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: isDarkTheme ? '#fff' : '#222',
                                        border: `1px solid ${isDarkTheme ? '#81c784' : '#2e7d32'}`
                                      }}>
                                        {i.key}:{i.value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                                {exitIndicators.length > 0 && (
                                  <div style={{ 
                                    color: isDarkTheme ? '#ef5350' : '#d32f2f', 
                                    fontWeight: '600',
                                    fontSize: '15px'
                                  }}>
                                    � Exit: {exitIndicators.map(i => (
                                      <span key={i.key} style={{ 
                                        backgroundColor: isDarkTheme ? '#5d1a1a' : '#ffebee',
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        marginRight: '6px',
                                        fontFamily: 'monospace',
                                        fontSize: '15px',
                                        fontWeight: 600,
                                        color: isDarkTheme ? '#fff' : '#222',
                                        border: `1px solid ${isDarkTheme ? '#ef5350' : '#d32f2f'}`
                                      }}>
                                        {i.key}:{i.value}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })()
                        : <span style={{ color: theme?.textSecondary || '#999', fontStyle: 'italic', fontSize: '10px' }}>No data</span>
                      }
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Table Footer with Summary */}
              <div style={{
                padding: '12px 15px',
                backgroundColor: isDarkTheme ? '#404040' : '#f8f9fa',
                borderTop: `1px solid ${theme?.border || '#e0e0e0'}`,
                fontSize: '12px',
                fontWeight: 600,
                color: theme?.textPrimary || '#333',
                display: 'flex',
                justifyContent: 'space-between'
              }}>
                <span>Total Trades: {results.trades.length}</span>
                <span>
                  Winning Trades: {results.trades.filter(t => t.pnl > 0).length} | 
                  Losing Trades: {results.trades.filter(t => t.pnl < 0).length}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Execution Time */}
      {results.execution_time !== undefined && (
        <div style={{
          marginTop: '20px',
          padding: '10px',
          backgroundColor: isDarkTheme ? '#1a2a3a' : '#f0f7ff',
          borderRadius: '4px',
          textAlign: 'center',
          fontSize: '12px',
          color: theme?.textSecondary || '#666'
        }}>
          ⚡ Execution Time: {results.execution_time.toFixed(3)} seconds
        </div>
      )}
    </div>
  );
};

export default ResultsDisplay;